import { Link } from "react-router-dom";
import { useState, useCallback } from "react";
import { useInvoices } from "../hooks/useInvoices";
import {
  DeleteInvoiceModal,
  EditInvoiceModal,
} from "../components/InvoiceActionsModal";

import TableHeader from "../components/TableHeader";
import TableBody from "../components/TableBody";
import TableFooter from "../components/TableFooter";
import ImportExportBar from "../../shared/ImportExportBar";
import SendEmailModal from "../components/SendEmailModal";
import apiFetch from "../../../../shared/services/apiClient";

import styles from "../styles/invoice.module.css";

const INITIAL_PARAMS = {
  search: "",
  fecha_desde: "",
  fecha_hasta: "",
  monto_min: "",
  monto_max: "",
  ordering: "",
};

export default function ListInvoice() {
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [params, setParams] = useState(INITIAL_PARAMS);
  const [importResult, setImportResult] = useState(null);

  const [deletingInvoice, setDeletingInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [actionMsg, setActionMsg] = useState(null);
  const [emailModal, setEmailModal] = useState(null);

  // ── Selección cross-page: Map<id, facturaObj> ──────────────────────
  // Guardamos el objeto completo para poder enviarlo al modal de correo
  // sin importar en qué página estaba cuando se seleccionó.
  const [selectedMap, setSelectedMap] = useState(new Map());
  const [selectingAll, setSelectingAll] = useState(false);

  const selectedIds = new Set(selectedMap.keys());
  

  function toggleSelect(id, invoiceObj) {
    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, invoiceObj);
      return next;
    });
  }

  // Seleccionar TODAS las facturas del filtro actual (todas las páginas)
  async function selectAll() {
    setSelectingAll(true);
    try {
      // Pedir sin paginación usando page_size grande
      const qs = buildAllQS(params);
      const res = await apiFetch(`/api/finance/factura/?${qs}`);
      const data = await res.json();
      const all = data.results ?? [];
      const map = new Map();
      all.forEach((inv) => map.set(inv.id_factura ?? inv.id, inv));
      setSelectedMap(map);
    } catch (_) {
    } finally {
      setSelectingAll(false);
    }
  }

  function clearSelection() {
    setSelectedMap(new Map());
  }

  // ──────────────────────────────────────────────────────────────────

  function handleParamsChange(newParams) {
    setParams(newParams);
    setPage(1);
    clearSelection(); // cambiar filtros limpia la selección
  }

  const { invoices, count, next, previous, loading } = useInvoices(
    page,
    params,
    refreshKey,
  );
  const totalPages = Math.ceil(count / 8);

  const handleImportSuccess = useCallback(() => {
    setPage(1);
    setRefreshKey((k) => k + 1);
  }, []);

  function handleResult(resultado) {
    setImportResult(resultado);
  }

  function handleDeleteSuccess() {
    setRefreshKey((k) => k + 1);
    setActionMsg({ ok: true, text: "Factura eliminada correctamente." });
    setTimeout(() => setActionMsg(null), 4000);
  }

  function handleEditSuccess(warning) {
    setRefreshKey((k) => k + 1);
    if (warning) {
      setActionMsg({ ok: false, text: warning });
    } else {
      setActionMsg({ ok: true, text: "Factura actualizada correctamente." });
      setTimeout(() => setActionMsg(null), 4000);
    }
  }

  return (
    <>
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>Inicio /</span>
        </Link>
        <Link to="/finanzas/cxc">
          <span className={styles.inactive}> Cuentas por Cobrar /</span>
        </Link>
        <span className={styles.active}> Facturas</span>
      </span>


      {/* ── Header ─────────────────────────────────────────────────── */}
      <div
        className="d-flex justify-content-between align-items-center flex-wrap gap-2"
        style={{ marginBottom: "6px" }}
      >
                <div>
          <h2 className="mt-2 me-2 p-0 title">Gestión de Facturas</h2>
          <p className="mb-3" style={{color:"#9ca3af"}}>Administra las facturas</p>
        </div>


        <div className="d-flex flex-column gap-2">
          <div className="d-flex gap-2 align-items-center flex-wrap justify-content-end">
            <ImportExportBar
              plantillaUrl="/api/finance/factura/plantilla-excel/"
              importarUrl="/api/finance/factura/importar-excel/"
              exportarUrl="/api/finance/factura/exportar-excel/"
              plantillaFilename="plantilla_facturas.xlsx"
              exportarFilename="facturas.xlsx"
              onImportSuccess={handleImportSuccess}
              onResult={handleResult}
            />
            <Link
              to="/finanzas/facturas/agregar"
              className={`btn ${styles["btn-add-factura"]} d-flex gap-2 align-items-center`}
            >
              <i className="fa-solid fa-plus" /> Agregar Factura
            </Link>
            
            <Link
              to="/finanzas/facturas/masivo"
              className={`btn ${styles["btn-add-factura"]} d-flex gap-2 align-items-center`}
            >
              <i className="fa-solid fa-layer-group" /> Facturas Masivas
            </Link>
          </div>
          <div className="d-flex gap-2 align-items-center justify-content-end flex-wrap">
            {selectedIds.size > 0 && (
              <>
                <span style={{ color: "#9ca3af", fontSize: "0.82rem" }}>
                  {selectedIds.size} seleccionado(s)
                </span>

                <button
                  className="btn"
                  onClick={clearSelection}
                  style={{
                    background: "transparent",
                    color: "#9ca3af",
                    border: "1px solid #374151",
                    fontSize: "0.8rem",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = "#ef4444";
                    e.currentTarget.style.color = "#ef4444";
                    e.currentTarget.style.background = "rgba(239,68,68,0.1)";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = "#374151";
                    e.currentTarget.style.color = "#9ca3af";
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <i className="fa-solid fa-xmark" style={{ marginRight: 4 }} />{" "}
                  Deseleccionar todas
                </button>
                <button
                  className="btn d-flex gap-2 align-items-center"
                  onClick={() =>
                    setEmailModal(Array.from(selectedMap.values()))
                  }
                  style={{
                    background: "#f97316",
                    color: "#fff",
                    border: "1px solid #f97316",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.backgroundColor = "#ea580c")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f97316")
                  }
                >
                  <i className="fa-solid fa-envelope" /> Enviar correos (
                  {selectedIds.size})
                </button>
              </>
            )}
            {selectedIds.size !== count && (
              <button
                className="btn d-flex gap-2 align-items-center"
                onClick={selectAll}
                disabled={selectingAll || count === 0}
                style={{
                  background: "rgba(55,65,81,0.4)",
                  color: "#9ca3af",
                  border: "none",
                  fontSize: "0.8rem",
                }}
                onMouseOver={(e) => (e.currentTarget.style.color = "#f9fafb")}
                onMouseOut={(e) => (e.currentTarget.style.color = "#9ca3af")}
              >
                {selectingAll ? (
                  <>
                    <i className="fa-solid fa-circle-notch fa-spin" />{" "}
                    Seleccionando…
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-check-double" /> Seleccionar todas
                    ({count})
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Banner acción ──────────────────────────────────────────── */}
      {actionMsg && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 16px",
            marginBottom: "12px",
            borderRadius: "8px",
            background: actionMsg.ok
              ? "rgba(74,222,128,0.07)"
              : "rgba(251,146,60,0.08)",
            border: `1px solid ${actionMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(251,146,60,0.3)"}`,
            borderLeft: `4px solid ${actionMsg.ok ? "#4ade80" : "#fb923c"}`,
          }}
        >
          <span
            style={{
              fontWeight: 500,
              fontSize: "0.86rem",
              color: actionMsg.ok ? "#bbf7d0" : "#fdba74",
            }}
          >
            <i
              className={`fa-solid ${actionMsg.ok ? "fa-circle-check" : "fa-triangle-exclamation"}`}
              style={{ marginRight: 8 }}
            />
            {actionMsg.text}
          </span>
          <button
            onClick={() => setActionMsg(null)}
            style={{
              background: "none",
              border: "none",
              color: "#6b7280",
              cursor: "pointer",
              fontSize: "1rem",
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Resultado importación ──────────────────────────────────── */}
      {importResult && (
        <div
          style={{
            width: "100%",
            borderRadius: "8px",
            marginBottom: "14px",
            background: importResult.ok
              ? "rgba(74,222,128,0.07)"
              : "rgba(248,113,113,0.07)",
            border: `1px solid ${importResult.ok ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`,
            borderLeft: `4px solid ${importResult.ok ? "#4ade80" : "#f87171"}`,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 16px",
              borderBottom:
                importResult.errores?.length > 0
                  ? "1px solid rgba(55,65,81,0.4)"
                  : "none",
            }}
          >
            <span
              style={{
                fontWeight: 600,
                fontSize: "0.88rem",
                color: importResult.ok ? "#bbf7d0" : "#fecaca",
              }}
            >
              {importResult.msg}
            </span>
            <button
              onClick={() => setImportResult(null)}
              style={{
                background: "none",
                border: "none",
                color: "#6b7280",
                cursor: "pointer",
                fontSize: "1rem",
              }}
            >
              ✕
            </button>
          </div>
          {importResult.errores?.length > 0 && (
            <div style={{ padding: "10px 16px" }}>
              <details>
                <summary
                  style={{
                    cursor: "pointer",
                    color: "#fb923c",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                  }}
                >
                  {importResult.errores.length} fila(s) con errores — clic para
                  ver
                </summary>
                <ul
                  style={{
                    marginTop: 8,
                    paddingLeft: 18,
                    color: "#fca5a5",
                    fontSize: "0.8rem",
                  }}
                >
                  {importResult.errores.map((e, i) => (
                    <li key={i}>
                      <strong>Fila {e.fila}:</strong>{" "}
                      {e.errores?.join(", ") ?? e}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          )}
        </div>
      )}

      {/* ── Tabla principal ────────────────────────────────────────── */}
      <div className={styles["table-container"]}>
        <TableHeader
          params={params}
          onParamsChange={handleParamsChange}
          loading={loading}
        />
        <TableBody
          invoices={invoices}
          onEdit={(inv) => setEditingInvoice(inv)}
          onDelete={(inv) => setDeletingInvoice(inv)}
          onEmail={(inv) => setEmailModal([inv])}
          selectedIds={selectedIds}
          onToggleSelect={(id, inv) => toggleSelect(id, inv)}
        />
        <TableFooter
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          next={next}
          previous={previous}
          count={count}
          loading={loading}
        />
      </div>

      {/* ── Modales ────────────────────────────────────────────────── */}
      {emailModal && (
        <SendEmailModal
          facturas={emailModal}
          onClose={() => setEmailModal(null)}
          onSuccess={() => {
            setActionMsg({
              ok: true,
              text: `Correo(s) enviado(s) correctamente.`,
            });
            clearSelection();
          }}
        />
      )}

      {deletingInvoice && (
        <DeleteInvoiceModal
          invoice={deletingInvoice}
          onClose={() => setDeletingInvoice(null)}
          onSuccess={handleDeleteSuccess}
        />
      )}

      {editingInvoice && (
        <EditInvoiceModal
          invoice={editingInvoice}
          onClose={() => setEditingInvoice(null)}
          onSuccess={handleEditSuccess}
        />
      )}
    </>
  );
}

// Construye query string sin paginación para traer todas las facturas del filtro
function buildAllQS(params) {
  const p = new URLSearchParams();
  p.set("page_size", "9999");
  if (params.search?.trim()) p.set("search", params.search.trim());
  if (params.fecha_desde?.trim())
    p.set("fecha_desde", params.fecha_desde.trim());
  if (params.fecha_hasta?.trim())
    p.set("fecha_hasta", params.fecha_hasta.trim());
  if (params.monto_min?.trim()) p.set("monto_min", params.monto_min.trim());
  if (params.monto_max?.trim()) p.set("monto_max", params.monto_max.trim());
  if (params.ordering?.trim()) p.set("ordering", params.ordering.trim());
  return p.toString();
}
